import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuChevronDown } from "react-icons/lu";
import { colors, typography } from "../../../design/tokens";

interface DropdownOption {
  value: string;
  label: string;
}

interface AppStoreRankingsDropdownProps {
  label?: string;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function AppStoreRankingsDropdown({
  label,
  options,
  value,
  onChange,
  className,
}: AppStoreRankingsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const lastKeyRef = useRef<string>("");
  const lastKeyTimeRef = useRef<number>(0);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedOption = options.find((o) => o.value === value);

  // Find first option starting with letter
  const findFirstMatch = (letter: string): number => {
    return options.findIndex((opt) =>
      opt.label.toLowerCase().startsWith(letter)
    );
  };

  // Find next option after current index starting with letter (with wrapping)
  const findNextMatch = (letter: string, currentIndex: number): number => {
    // Search from current+1 to end
    for (let i = currentIndex + 1; i < options.length; i++) {
      if (options[i].label.toLowerCase().startsWith(letter)) {
        return i;
      }
    }
    // Wrap around: search from start to current
    for (let i = 0; i <= currentIndex; i++) {
      if (options[i].label.toLowerCase().startsWith(letter)) {
        return i;
      }
    }
    return -1; // No match found
  };

  // Scroll focused option into view
  const scrollToIndex = (index: number) => {
    optionRefs.current[index]?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 200),
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initialize focus when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Find index of currently selected value
      const selectedIndex = options.findIndex((o) => o.value === value);
      setFocusedIndex(selectedIndex);
    } else {
      // Reset focus when closed
      setFocusedIndex(-1);
    }
  }, [isOpen, options, value]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Letter keys: Jump/cycle through matches
      if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
        e.preventDefault();
        const letter = e.key.toLowerCase();
        const now = Date.now();
        const isSameKey =
          letter === lastKeyRef.current &&
          now - lastKeyTimeRef.current < 500;

        let nextIndex: number;
        if (isSameKey) {
          // Find next match after current focusedIndex
          nextIndex = findNextMatch(letter, focusedIndex);
        } else {
          // Find first match
          nextIndex = findFirstMatch(letter);
        }

        if (nextIndex !== -1) {
          setFocusedIndex(nextIndex);
          scrollToIndex(nextIndex);
        }

        lastKeyRef.current = letter;
        lastKeyTimeRef.current = now;
      }
      // Arrow keys: Navigate one by one with wrapping
      else if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((prev) => {
          const next = prev + 1 >= options.length ? 0 : prev + 1;
          scrollToIndex(next);
          return next;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((prev) => {
          const next = prev - 1 < 0 ? options.length - 1 : prev - 1;
          scrollToIndex(next);
          return next;
        });
      }
      // Home/End: Jump to first/last
      else if (e.key === "Home") {
        e.preventDefault();
        setFocusedIndex(0);
        scrollToIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        const lastIndex = options.length - 1;
        setFocusedIndex(lastIndex);
        scrollToIndex(lastIndex);
      }
      // Enter: Select focused option
      else if (e.key === "Enter" && focusedIndex !== -1) {
        e.preventDefault();
        onChange(options[focusedIndex].value);
        setIsOpen(false);
      }
      // Escape: Close dropdown
      else if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, focusedIndex, options, onChange]);

  return (
    <div className={`relative ${className || ""}`}>
      {label && (
        <span
          className={`${typography.size.xs} ${colors.text.secondary} mb-1 block uppercase tracking-wider`}
        >
          {label}
        </span>
      )}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between gap-2 px-4 py-3 w-full
          bg-dark-surface-secondary border border-dark-border rounded-xl
          ${typography.size.sm} ${colors.text.primary}
          transition-colors hover:border-white/30 focus:border-primary-blue focus:outline-none`}
      >
        <span className="truncate">{selectedOption?.label || "Select..."}</span>
        <LuChevronDown
          size={16}
          className={`transition-transform flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: position.width,
            }}
            className="z-50 bg-dark-surface-secondary border border-dark-border rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.4)] max-h-[300px] overflow-y-auto"
          >
            {options.map((option, index) => (
              <button
                key={`${option.value}-${index}`}
                ref={(el) => (optionRefs.current[index] = el)}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 ${typography.size.sm} transition-colors ${
                  value === option.value
                    ? "bg-primary-blue/20 text-white"
                    : `${colors.text.primary} hover:bg-white/10`
                } ${
                  focusedIndex === index
                    ? "ring-1 ring-primary-blue bg-white/5"
                    : ""
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
